/**
 * Forgot Password Route
 *
 * Page for requesting password reset email.
 */

import * as React from "react";
import { createFileRoute } from "@tanstack/react-router";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
  Input,
  Button,
} from "@provenleap/ui";

export const Route = createFileRoute("/_auth/forgot-password")({
  component: ForgotPasswordPage,
});

function ForgotPasswordPage() {
  const [email, setEmail] = React.useState("");
  const [isLoading, setIsLoading] = React.useState(false);
  const [isSubmitted, setIsSubmitted] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || "";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email) {
      setError("Please enter your email address.");
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`${apiBaseUrl}/api/auth/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });

      if (response.status === 429) {
        // Rate limited
        const data = await response.json();
        const retryAfter = data.retryAfter;
        if (retryAfter && retryAfter > 60) {
          const minutes = Math.ceil(retryAfter / 60);
          setError(`Too many requests. Please try again in ${minutes} minute${minutes !== 1 ? "s" : ""}.`);
        } else {
          setError("Too many requests. Please try again later.");
        }
      } else if (response.ok) {
        // Success - always show success message to prevent user enumeration
        setIsSubmitted(true);
      } else {
        // Handle validation errors
        const data = await response.json();
        if (data.type?.includes("validation-error")) {
          setError("Please enter a valid email address.");
        } else {
          setError("Unable to send reset email. Please try again.");
        }
      }
    } catch {
      setError("Unable to connect. Please check your internet connection.");
    } finally {
      setIsLoading(false);
    }
  };

  if (isSubmitted) {
    return (
      <Card className="!rounded-[22px] shadow-sm" style={{ border: "0.5px solid var(--hairline)" }}>
        <CardHeader>
          <CardTitle>Check your email</CardTitle>
          <CardDescription className="mt-2 text-neutral-700">
            If an account exists with this email, you'll receive a password reset link.
          </CardDescription>
          <div className="mt-4 mx-auto rounded-full border border-border px-4 py-2">
            <span className="text-sm font-medium text-foreground">{email}</span>
          </div>
          <p className="mt-5 text-sm text-neutral-700 text-center">
            The link will expire in 1 hour. Don't forget to check your spam folder.
          </p>
        </CardHeader>
        <CardFooter className="flex justify-center">
          <a href="/login" className="text-sm font-medium text-primary hover:underline flex items-center gap-1">
            <span>←</span> Back to login
          </a>
        </CardFooter>
      </Card>
    );
  }

  return (
    <Card className="!rounded-[22px] shadow-sm" style={{ border: "0.5px solid var(--hairline)" }}>
      <CardHeader>
        <CardTitle>Forgot Password?</CardTitle>
        <CardDescription className="mt-2 text-neutral-700">
          Enter your email to receive a password reset link
        </CardDescription>
      </CardHeader>
      <CardContent className="pt-0">
        {error && (
          <div className="rounded-md bg-error/10 p-3 text-sm text-error mb-4">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Input
              id="email"
              type="email"
              className="rounded-lg"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isLoading}
              autoComplete="email"
            />
          </div>

          <Button type="submit" variant="brand" className="w-full cursor-pointer" disabled={isLoading}>
            {isLoading ? "Sending..." : "Send reset link"}
          </Button>
        </form>
      </CardContent>
      <CardFooter className="flex justify-center">
        <a href="/login" className="text-sm font-medium text-primary hover:underline flex items-center gap-1">
          <span>←</span> Back to login
        </a>
      </CardFooter>
    </Card>
  );
}
